import { IOrder, IOrderClubMember } from '@/interfaces/IOrder';

/**
 * Pure resolver for the printed-ticket Club points lines:
 *
 *   PUNTOS CLUB: +1.250
 *   TOTAL PUNTOS: 10.500
 *
 * Accepts one or many orders (the group-payment doc prints one combined
 * ticket); earn is aggregated per member and a member name suffix is added
 * only when the group spans several members. Short ASCII-only lines so they
 * fit 58 mm printers (32 chars) and survive every supported codepage.
 *
 * Data source semantics (CRITICAL): `clubMember.earnedPoints` is a
 * server-attached transient that sums the order's OWN earn/revoke ledger
 * entries — it is 0 until the order is Paid, so these lines can only appear
 * on post-payment documents and never show a stale pre-payment balance.
 *
 * - `label` EXCLUDES the trailing colon; consumers print
 *   `encoder.keyValue(`${label}:`, valueDisplay)`.
 * - Legacy payloads without `earnedPoints` yield no lines.
 */
export interface IClubTicketLine {
  label: string;
  valueDisplay: string;
}

export function computeClubTicketLines(
  orders: Array<Pick<IOrder, 'clubMember'>>
): IClubTicketLine[] {
  const byMember = new Map<
    number,
    { member: IOrderClubMember; earned: number }
  >();

  for (const order of orders) {
    const member = order.clubMember;
    if (!member) continue;

    // Accumulate net earn (revokes included) so a refunded order inside a
    // group correctly offsets the others.
    const earned = Number(member.earnedPoints ?? 0);
    const bucket = byMember.get(member.id);
    if (bucket) bucket.earned += earned;
    else byMember.set(member.id, { member, earned });
  }

  const lines: IClubTicketLine[] = [];
  const positive = [...byMember.values()].filter(
    (bucket) => bucket.earned > 0
  );
  const multi = positive.length > 1;

  for (const { member, earned } of positive) {
    const suffix = multi ? ` ${member.name}` : '';
    lines.push({
      label: `PUNTOS CLUB${suffix}`,
      valueDisplay: `+${earned.toLocaleString('es-AR')}`,
    });
    lines.push({
      label: `TOTAL PUNTOS${suffix}`,
      valueDisplay: Number(member.pointsBalance ?? 0).toLocaleString('es-AR'),
    });
  }

  return lines;
}
